import { makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { ArrowRight16Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { dolphinTokens } from "../../utils/Theme";

const featuredBackground = "#0B3C49";
const featuredAccent = "#7FD4E0";
const featuredBody = "#C9DDE3";

const useStyles = makeStyles({
    root: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "stretch",
        gap: "20px",
        maxWidth: "1312px",
        margin: "2rem auto 3rem",
        padding: "0 1rem",
        boxSizing: "border-box",
    },
    grid: {
        flex: "1.7 1 560px",
        minWidth: 0,
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: "20px",
        "@media (max-width: 640px)": {
            gridTemplateColumns: "minmax(0, 1fr)",
        },
    },

    // Standard card
    card: {
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        padding: "28px",
        boxSizing: "border-box",
        backgroundColor: tokens.colorNeutralBackground1,
        border: `1px solid ${dolphinTokens.colorLightBlue}`,
        borderRadius: "16px",
        transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
        "&:hover": {
            transform: "translateY(-3px)",
            border: `1px solid ${dolphinTokens.colorLightBlue}`,
            boxShadow: "0 12px 28px rgba(15, 36, 48, 0.10)",
        },
        "&:has(a:focus-visible)": {
            outlineWidth: "3px",
            outlineStyle: "solid",
            outlineColor: dolphinTokens.colorLightBlue,
            outlineOffset: "3px",
        },
        "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
            "&:hover": { transform: "none" },
        },
    },
    tile: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "48px",
        height: "48px",
        borderRadius: "12px",
        fontSize: "1.5rem",
        color: dolphinTokens.colorLightBlue,
        backgroundColor: `color-mix(in srgb, ${dolphinTokens.colorLightBlue} 12%, white)`,
    },
    title: {
        margin: "6px 0 0",
        fontSize: tokens.fontSizeBase500,
        lineHeight: tokens.lineHeightBase500,
        fontWeight: tokens.fontWeightBold,
        color: tokens.colorNeutralForeground1,
    },
    description: {
        margin: 0,
        fontSize: tokens.fontSizeBase400,
        lineHeight: "1.6",
        color: tokens.colorNeutralForeground2,
    },
    cta: {
        marginTop: "auto",
        paddingTop: "8px",
        display: "inline-flex",
        alignItems: "center",
        alignSelf: "flex-start",
        gap: "8px",
        fontSize: tokens.fontSizeBase400,
        fontWeight: tokens.fontWeightSemibold,
        color: dolphinTokens.colorLightBlue,
        textDecorationLine: "none",
        "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            borderRadius: "inherit",
        },
        "&:hover": { textDecorationLine: "none" },
        "&:focus-visible": { outlineStyle: "none" },
        "& svg": { transition: "transform 0.18s ease" },
        "&:hover svg": { transform: "translateX(4px)" },
        "@media (prefers-reduced-motion: reduce)": {
            "& svg": { transition: "none" },
            "&:hover svg": { transform: "none" },
        },
    },

    // Featured card overrides
    featuredCard: {
        flex: "1 1 400px",
        gap: "16px",
        padding: "40px",
        borderRadius: "20px",
        backgroundColor: featuredBackground,
        border: `1px solid ${featuredBackground}`,
        overflow: "hidden",
        "&:hover": {
            border: `1px solid ${featuredBackground}`,
            boxShadow: "0 16px 36px rgba(11, 60, 73, 0.28)",
        },
        "&:has(a:focus-visible)": {
            outlineColor: featuredAccent,
        },
    },
    featuredTile: {
        width: "56px",
        height: "56px",
        borderRadius: "14px",
        fontSize: "1.875rem",
        color: featuredAccent,
        backgroundColor: "rgba(255, 255, 255, 0.12)",
    },
    featuredBadge: {
        margin: "12px 0 0",
        fontSize: "0.8125rem",
        fontWeight: tokens.fontWeightBold,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: featuredAccent,
    },
    featuredTitle: {
        margin: 0,
        fontSize: tokens.fontSizeHero800,
        lineHeight: tokens.lineHeightHero800,
        color: "white",
    },
    featuredDescription: {
        maxWidth: "36rem",
        fontSize: "1.125rem",
        color: featuredBody,
    },

    featuredWatermark: {
        position: "absolute",
        right: "-24px",
        bottom: "-40px",
        display: "flex",
        fontSize: "15rem",
        color: featuredAccent,
        opacity: 0.08,
        pointerEvents: "none",
    },
    featuredCta: {
        color: "white",
    },
});

interface IServiceCardProps {
    title: string;
    description: string;
    linkUrl: string;
    linkText: string;
    icon: React.ReactNode;
    featured?: boolean;
    badge?: string;
}

const ServiceCard = (props: IServiceCardProps) => {
    const { title, description, linkUrl, linkText, icon, featured, badge } = props;
    const styles = useStyles();

    return (
        <article className={mergeClasses(styles.card, featured && styles.featuredCard)}>
            <div
                className={mergeClasses(styles.tile, featured && styles.featuredTile)}
                aria-hidden="true"
            >
                {icon}
            </div>
            {featured && (
                <div className={styles.featuredWatermark} aria-hidden="true">
                    {icon}
                </div>
            )}
            {featured && badge && <p className={styles.featuredBadge}>{badge}</p>}
            <h3 className={mergeClasses(styles.title, featured && styles.featuredTitle)}>{title}</h3>
            <p className={mergeClasses(styles.description, featured && styles.featuredDescription)}>
                {description}
            </p>
            <a className={mergeClasses(styles.cta, featured && styles.featuredCta)} href={linkUrl}>
                {linkText}
                <ArrowRight16Regular aria-hidden="true" />
            </a>
        </article>
    );
};

export interface IKeyFeaturesCard {
    icon: React.ReactNode;
    titleKey: string;
    descriptionKey: string;
    linkUrl: string;
    linkTextKey: string;
    featured?: boolean;
    badgeKey?: string;
}

interface IKeyFeaturesProps {
    cards: IKeyFeaturesCard[];
}

export default function KeyFeatures({ cards }: IKeyFeaturesProps) {
    const { t } = useTranslation("home");
    const styles = useStyles();

    const featured = cards.find((card) => card.featured);
    const rest = featured ? cards.filter((card) => card !== featured) : cards;

    const renderCard = (card: IKeyFeaturesCard, isFeatured: boolean) => (
        <ServiceCard
            key={card.titleKey}
            icon={card.icon}
            title={t(card.titleKey)}
            description={t(card.descriptionKey)}
            linkUrl={card.linkUrl}
            linkText={t(card.linkTextKey)}
            featured={isFeatured}
            badge={card.badgeKey ? t(card.badgeKey) : undefined}
        />
    );

    return (
        <div className={styles.root}>
            {featured && renderCard(featured, true)}
            <div className={styles.grid}>{rest.map((card) => renderCard(card, false))}</div>
        </div>
    );
}